/**
 * Per-platform profile copy: field sets, labels and character limits for the
 * Social profiles panels (Home, Settings, client portal Bio). Limits are soft
 * (warn, never block): only Google and the Instagram bio are officially
 * documented; FB and TikTok come from third-party trackers and moved within
 * the last year. Copy is bilingual because the client portal reads in Italian.
 */

import { SocialPlatform, SocialProfile, SocialProfileField, SocialProfiles } from "@/types";
import type { Locale } from "@/hooks/useLocale";

type Copy = Record<Locale, string>;

export const SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  "instagram",
  "facebook",
  "tiktok",
  "google",
];

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  google: "Google",
};

const ALL_FIELDS: readonly SocialProfileField[] = ["name", "handle", "bio", "website", "url"];

// Stored in their own clients columns, so the client portal shows them but
// never edits them (the public case study page publishes the IG handle).
const COLUMN_OWNED: Partial<Record<SocialPlatform, SocialProfileField>> = {
  instagram: "handle",
  facebook: "name",
};

const LIMITS: Record<SocialPlatform, Partial<Record<SocialProfileField, number>>> = {
  instagram: { name: 30, handle: 30, bio: 150 },
  facebook: { name: 75, handle: 50, bio: 101 },
  tiktok: { name: 30, handle: 24, bio: 160 },
  google: { bio: 750 },
};

const DEFAULT_LABELS: Record<SocialProfileField, Copy> = {
  name: { en: "Name", it: "Nome" },
  handle: { en: "Handle", it: "Nome utente" },
  bio: { en: "Bio", it: "Bio" },
  website: { en: "Website", it: "Sito web" },
  url: { en: "Profile URL", it: "URL del profilo" },
};

const LABEL_OVERRIDES: Partial<Record<SocialPlatform, Partial<Record<SocialProfileField, Copy>>>> = {
  facebook: { name: { en: "Page name", it: "Nome della pagina" } },
  google: {
    name: { en: "Business name", it: "Nome dell'attività" },
    bio: { en: "Business description", it: "Descrizione dell'attività" },
    url: { en: "Maps listing", it: "Scheda Maps" },
  },
};

const HINTS: Partial<Record<SocialPlatform, Partial<Record<SocialProfileField, Copy>>>> = {
  google: {
    name: {
      en: "Must match the real-world signage. Changing it can trigger re-verification.",
      it: "Deve corrispondere all'insegna reale. Cambiarlo può richiedere una nuova verifica.",
    },
    bio: {
      en: "Only the first 250 characters show. No URLs, prices or promos.",
      it: "Si vedono solo i primi 250 caratteri. Niente URL, prezzi o promozioni.",
    },
  },
};

/** Chrome copy for the panels and the edit drawer. */
export const PROFILE_COPY: Record<Locale, {
  notSet: string;
  drawerTitle: (platform: SocialPlatform) => string;
  save: string;
  saving: string;
  saveFailed: string;
  badUrl: string;
}> = {
  en: {
    notSet: "Not set",
    drawerTitle: (p) => `${PLATFORM_LABELS[p]} profile`,
    save: "Save",
    saving: "Saving...",
    saveFailed: "Failed to save profile",
    badUrl: "Must start with http:// or https://",
  },
  it: {
    notSet: "Non impostato",
    drawerTitle: (p) => `Profilo ${PLATFORM_LABELS[p]}`,
    save: "Salva",
    saving: "Salvataggio...",
    saveFailed: "Salvataggio non riuscito",
    badUrl: "Deve iniziare con http:// o https://",
  },
};

export const fieldsFor = (platform: SocialPlatform): SocialProfileField[] =>
  ALL_FIELDS.filter((f) => !(platform === "google" && f === "handle"));

/** The fields a client may edit from the portal: all but the column-owned ones. */
export const clientFieldsFor = (platform: SocialPlatform): SocialProfileField[] =>
  fieldsFor(platform).filter((f) => COLUMN_OWNED[platform] !== f);

export const fieldLabel = (
  platform: SocialPlatform,
  field: SocialProfileField,
  locale: Locale = "en",
): string => (LABEL_OVERRIDES[platform]?.[field] ?? DEFAULT_LABELS[field])[locale];

export const fieldHint = (
  platform: SocialPlatform,
  field: SocialProfileField,
  locale: Locale = "en",
): string | null => HINTS[platform]?.[field]?.[locale] ?? null;

export const limitFor = (platform: SocialPlatform, field: SocialProfileField): number | null =>
  LIMITS[platform][field] ?? null;

export const isOverLimit = (value: string, limit: number | null): boolean =>
  limit !== null && value.length > limit;

export const isUrlField = (field: SocialProfileField): boolean =>
  field === "website" || field === "url";

export const isValidProfileUrl = (value: string): boolean =>
  value === "" || /^https?:\/\/\S+$/i.test(value);

export const emptySocialProfiles = (): SocialProfiles => ({
  instagram: {},
  facebook: {},
  tiktok: {},
  google: {},
});

export const withPlatform = (
  profiles: SocialProfiles,
  platform: SocialPlatform,
  profile: SocialProfile,
): SocialProfiles => ({ ...profiles, [platform]: profile });
